import React, { useState } from 'react';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { Button, Box, Typography, Paper } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsIcon from '@mui/icons-material/Directions';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const LocationMap = ({ 
  latitude, 
  longitude, 
  name, 
  address,
  googleMapsUrl, 
  mapHeight = 250, 
  showButton = true
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const [map, setMap] = useState(null);
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);

  const containerStyle = {
    width: '100%',
    height: typeof mapHeight === 'number' ? `${mapHeight}px` : mapHeight,
    borderRadius: '8px',
    overflow: 'hidden'
  };

  const center = {
    lat: parseFloat(latitude),
    lng: parseFloat(longitude)
  };

  const options = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'cooperative'
  };

  const onLoad = React.useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    setMap(null);
  }, []);

  const googleMapsLink = googleMapsUrl
    ? googleMapsUrl
    : `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

  const toggleInfoWindow = () => {
    setIsInfoWindowOpen(!isInfoWindowOpen);
  };

  return (
    <>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={15}
          options={options}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          <Marker 
            position={center}
            onClick={toggleInfoWindow}
          >
            {isInfoWindowOpen && (
              <InfoWindow 
                position={center} 
                onCloseClick={toggleInfoWindow}
              >
                <Paper 
                  elevation={0} 
                  sx={{ 
                    padding: '8px 12px', 
                    maxWidth: '250px' 
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 0.5,
                      color: 'primary.main'
                    }}
                  >
                    {name}
                  </Typography>
                  
                  {address && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '12px', 
                        mb: 1,
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'flex-start'
                      }}
                    >
                      <LocationOnIcon 
                        sx={{ 
                          fontSize: '14px', 
                          mr: 0.5,
                          mt: '2px',
                          color: 'text.secondary'
                        }} 
                      />
                      {address}
                    </Typography>
                  )}
                  
                  <Button
                    variant="text"
                    size="small"
                    href={googleMapsLink}
                    target="_blank"
                    startIcon={<DirectionsIcon />}
                    sx={{ 
                      fontSize: '12px', 
                      padding: '2px 8px',
                      mt: 0.5
                    }}
                  >
                    Get Directions
                  </Button>
                </Paper>
              </InfoWindow>
            )}
          </Marker>
        </GoogleMap>
      ) : (
        <Box 
          sx={{ 
            height: typeof mapHeight === 'number' ? `${mapHeight}px` : mapHeight, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            backgroundColor: 'action.hover',
            borderRadius: '8px'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading map...
          </Typography>
        </Box>
      )}
      
      {showButton && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '10px',
            width: '100%'
          }}
        >
          <Button
            variant="outlined"
            href={googleMapsLink}
            target="_blank"
            startIcon={<DirectionsIcon />}
            size="small"
          >
            Open in Google Maps
          </Button>
        </Box>
      )}
    </>
  );
};

export default React.memo(LocationMap);